// Tabs
// Enhances a set of interleaved [role="tab"] buttons and [role="tabpanel"] panels
// (one pair per _tab-item block) into an accessible tabs widget: it builds the
// [role="tablist"], wires selection, roving tabindex and arrow-key navigation.
class TabsCustom extends HTMLElement {
  #controller = new AbortController();
  #list = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#build();

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    if (!this.#list) return [];
    return Array.from(this.#list.querySelectorAll(':scope > [role="tab"]'));
  }

  /**
   * Moves the direct-child tab buttons into a generated tablist and selects the initial tab.
   */
  #build() {
    const tabs = Array.from(this.querySelectorAll(':scope > [role="tab"]'));
    if (tabs.length === 0) return;

    let list = this.querySelector(':scope > [role="tablist"]');
    if (!list) {
      list = document.createElement('div');
      list.className = 'tabs__list';
      list.setAttribute('role', 'tablist');
      this.prepend(list);
    }
    for (const tab of tabs) list.appendChild(tab);
    this.#list = list;

    const active = tabs.find((tab) => tab.dataset.active === 'true') ?? tabs[0];
    this.#select(active, { focus: false });
  }

  /**
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #select(tab, { focus = true } = {}) {
    for (const current of this.tabs) {
      const selected = current === tab;
      current.setAttribute('aria-selected', selected ? 'true' : 'false');
      current.tabIndex = selected ? 0 : -1;

      const panel = this.#panelFor(current);
      if (panel) panel.hidden = !selected;
    }

    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (target instanceof HTMLElement && this.tabs.includes(target)) this.#select(target);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const tabs = this.tabs;
    const current = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(current instanceof HTMLElement) || !tabs.includes(current)) return;

    const index = tabs.indexOf(current);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next);
  };
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
